import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import noUiSlider from "nouislider";

import { Field, useFormikContext } from "formik";
import M from "materialize-css";

import Row from "../shared/Row";
import CardPanel from "../shared/CardPanel";
import CardTitle from "../shared/CardSearchTable/Title";
import CardHeader from "../shared/CardHeader";
import InputField from "../shared/InputField";

import { formattedCost } from "../../helpers/formatters";

const adjustmentSteps = [
  -95, -90,
  -85, -80,
  -75, -70,
  -65, -60,
  -55, -50,
  -45, -40,
  -35, -30,
  -25, -20,
  -15, -10,
  -5, 0, 5,
  10, 15,
  20, 25,
  30, 35,
  40, 45,
  50, 55,
  60, 65,
  70, 75,
  80, 85,
  90, 95,
];

const CardHeaderH5 = ({ icon, children, text, value }) => {
  const h5right = <h5 className="right">{value}</h5>;
  return (
    <Row>
      <div className="col s12">
        <h5 className="left card-panel-header">
          <i className="material-icons vertical-align-bottom">{icon}</i>
          &nbsp;{text}
        </h5>
        {children || h5right}
      </div>
    </Row>
  );
};
CardHeaderH5.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  value: PropTypes.string,
  children: PropTypes.node,
};

const AdjustmentSelector = () => {
  const ref = useRef();

  useEffect(() => {
    M.FormSelect.init(ref.current);
  }, []);

  return (
    <div className="right">
      <Field name="adjustmentFactor" as="select" innerRef={ref}>
        {adjustmentSteps.map((x) => {
          return (
            <option key={x} value={x}>
              {x}
            </option>
          );
        })}
      </Field>
    </div>
  );
};

function calculateAsset(employees, a) {
  if (a.touched) {
    return Number(a.cost);
  }
  return a.calculateBy === "EMPLOYEE" ? a.amount * employees : a.amount;
}

function recalculateAssets(employees, assets) {
  return assets.map((a) => {
    const cost = calculateAsset(employees, a);
    return { ...a, cost };
  });
}

const makeSliderProps = (start) => ({
  handleChange: console.log,
  clickablePips: true,
  start,
  step: 1,
  range: {
    min: 1,
    max: 20,
  },
  pips: {
    mode: "steps",
    filter: () => 1,
    density: 4,
  },
});

export default function MetaData() {
  const { resetForm, values, setFieldValue } = useFormikContext();
  const { employees, subtotal, assets, ...valueData } = values;

  const sliderRef = useRef(null);
  const [slider, setSlider] = useState();

  const updateSliderValue = useCallback((value) => {
    if (value === employees) {
      return;
    }
    const selectedValue = Number(value[0]);

    // const {assets, ...valueData} = values;
    // @TODO refactor estimate code into shared lib
    const updatedAssets = recalculateAssets(value, assets);
    const subtotal = updatedAssets.reduce((sum, a) => {
      return sum + a.cost;
    }, 0);
    const updatedValues = {
      ...valueData,
      subtotal,
      employees: selectedValue,
      assets: updatedAssets,
    };

    resetForm({ values: updatedValues });
  }, [employees, assets, valueData]);

  useEffect(() => {
    setSlider((slider) => {
      if (slider) return slider;

      return noUiSlider.create(
        sliderRef.current,
        makeSliderProps(employees),
      );
    });
  }, [slider, sliderRef.current, employees]);

  useEffect(() => {
    if (!slider) {
      return;
    }

    slider.on("slide.effect", updateSliderValue);
    slider.on("end.effect", updateSliderValue);
    return () => slider.off(".effect");
  }, [slider, updateSliderValue]);

  const total = values.subtotal * (1 + values.adjustmentFactor / 100);
  useEffect(() => {
    if (total !== values.total) {
      setFieldValue("total", total);
    }
  }, [total, values.total]);

  return (
    <CardPanel>
      <Row>
        <div className="col s3">
          <h5 className="left card-panel-header">
            <i className="material-icons vertical-align-bottom">group</i>
            &nbsp;# of Employees
          </h5>
        </div>
        <div className="col s9">
          <div className="row range-slider-container">
            <div className="col s10 range-slider-column">
              <div ref={sliderRef} />
            </div>
            <div className="col s2 text-center">
              <span className="range-slider-value">{employees}</span>
            </div>
          </div>
        </div>
      </Row>
      <CardHeaderH5
        text="Subtotal"
        icon="group"
        value={formattedCost(subtotal)}
      />
      <CardHeaderH5 text="Adjustment Factor" icon="exposure">
        <AdjustmentSelector />
      </CardHeaderH5>
      <CardHeaderH5
        text="Total"
        icon="monetization_on"
        value={formattedCost(Math.floor(total))}
      />
    </CardPanel>
  );
}
